// A local folder is a FileSystemDirectoryHandle the browser gave us. It
// cannot be written as text, so the handles of the folders in "Saved
// Connections" are kept in IndexedDB, under the id of the connection. The
// rest of a saved connection, its id and name, is in local storage with the
// other connections.
//
// A handle that comes back from IndexedDB has lost its permission: the
// browser asks the user again, and only in answer to a click or a key press.

const dbName = "fm-local-folders";
const storeName = "handles";

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let req = indexedDB.open(dbName, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(storeName);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("cannot open the browser's database"));
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  let db = await openDB();
  try {
    return await new Promise<T>((resolve, reject) => {
      let tx = db.transaction(storeName, mode);
      let req = fn(tx.objectStore(storeName));
      // a write is done when the transaction is, not when the request is
      tx.oncomplete = () => resolve(req.result);
      tx.onerror = () => reject(tx.error || req.error || new Error("the browser's database failed"));
      tx.onabort = () => reject(tx.error || new Error("the browser's database failed"));
    });
  } finally {
    db.close();
  }
}

export async function saveLocalHandle(fsID: string, handle: FileSystemDirectoryHandle): Promise<void> {
  await withStore("readwrite", (store) => store.put(handle, fsID));
}

// Returns the handle saved for a connection, or null if there is none.
export async function loadLocalHandle(fsID: string): Promise<FileSystemDirectoryHandle | null> {
  let h = await withStore<any>("readonly", (store) => store.get(fsID));
  return h || null;
}

export async function deleteLocalHandle(fsID: string): Promise<void> {
  await withStore("readwrite", (store) => store.delete(fsID));
}

// Makes sure we may read and write the folder, asking the user if needed.
// Must be called in answer to a click or a key press. Returns false if the
// user said no.
export async function ensureLocalPermission(handle: FileSystemDirectoryHandle): Promise<boolean> {
  // not in the types of every TypeScript version
  let h = handle as any;
  let opts = { mode: "readwrite" };
  if (!h.queryPermission || !h.requestPermission) {
    return true;
  }
  if ((await h.queryPermission(opts)) == "granted") {
    return true;
  }
  return (await h.requestPermission(opts)) == "granted";
}
